import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";

const FORM_URL = "https://readdy.ai/api/form/da24d0jk8r5te5mqsgbg";

const contacts = [
  { icon: "ri-mail-line", title: "邮箱", value: "support@xukong-comics.example" },
  { icon: "ri-wechat-line", title: "公众号", value: "虚空漫画网" },
  { icon: "ri-time-line", title: "反馈时间", value: "工作日 9:00 - 18:00" },
  { icon: "ri-chat-3-line", title: "合作洽谈", value: "cooperation@xukong-comics.example" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const ok = res.ok && parsed?.code === "OK";
      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(serverMsg && typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后再试。");
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，请稍后再试。");
      setStatus("error");
    }
  };

  return (
    <div className="pt-16 md:pt-[72px]">
      <Seo
        title="联系我们 | 虚空漫画网"
        description="联系虚空漫画网，进行作品推荐、纠错反馈、合作洽谈或追更疑问咨询。我们的客服工作日 9:00-18:00 在线回复。"
        path="/contact"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "联系我们",
            url: "https://talespots.github.io/contact",
            description: "虚空漫画网的联系方式与反馈表单。",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://talespots.github.io" },
              { "@type": "ListItem", position: 2, name: "联系我们", item: "https://talespots.github.io/contact" },
            ],
          },
        ]}
      />

      <section className="relative overflow-hidden border-b border-background-800 bg-background-900/60">
        <div className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full bg-primary-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-4 py-16 text-center md:px-6 md:py-20">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-background-950/50 px-4 py-1.5 text-xs font-medium text-primary-300">
            <i className="ri-chat-heart-line text-sm" />
            联系我们
          </span>
          <h1 className="mt-6 font-heading text-3xl font-black text-foreground-950 md:text-5xl">
            想聊点什么？<span className="text-gradient-gold">欢迎来信</span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-sm text-foreground-500 md:text-base">
            作品推荐、纠错反馈、合作洽谈，或只是想聊聊你最近追的漫画，都欢迎通过下方表单联系我们。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {contacts.map((c) => (
            <div key={c.title} className="rounded-xl border border-background-800 bg-background-900 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500/15 text-xl text-primary-400">
                <i className={c.icon} />
              </span>
              <h2 className="mt-4 text-sm font-semibold text-foreground-500">{c.title}</h2>
              <p className="mt-1 break-all font-heading text-sm font-bold text-foreground-950">{c.value}</p>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-12 max-w-2xl">
          <div className="rounded-2xl border border-background-800 bg-background-900 p-6 md:p-8">
            <h2 className="font-heading text-xl font-black text-foreground-950">给我们留言</h2>
            <p className="mt-2 text-sm text-foreground-500">填写下方表单，我们会尽快回复你。</p>

            {status === "success" && (
              <div className="mt-5 flex items-center gap-3 rounded-xl border border-primary-500/40 bg-primary-500/10 px-4 py-3 text-sm text-primary-300">
                <i className="ri-checkbox-circle-fill text-lg" />
                已收到你的留言，感谢反馈，我们会尽快处理！
              </div>
            )}
            {status === "error" && (
              <div className="mt-5 flex items-center gap-3 rounded-xl border border-accent-500/40 bg-accent-500/10 px-4 py-3 text-sm text-accent-400">
                <i className="ri-error-warning-fill text-lg" />
                {formError}
              </div>
            )}

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={onSubmit}
              className="mt-6 space-y-4"
            >
              <div className="honeypot-guard">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">你的称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={30}
                    placeholder="怎么称呼你"
                    className="w-full rounded-lg border border-background-700 bg-background-950 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full rounded-lg border border-background-700 bg-background-950 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">留言主题</label>
                <select
                  id="subject"
                  name="subject"
                  className="w-full cursor-pointer rounded-lg border border-background-700 bg-background-950 px-4 py-2.5 text-sm text-foreground-900 focus:border-primary-500 focus:outline-none"
                >
                  <option value="作品推荐">作品推荐</option>
                  <option value="纠错反馈">纠错反馈</option>
                  <option value="追更咨询">追更咨询</option>
                  <option value="合作洽谈">合作洽谈</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="想推荐哪部漫画？发现了什么问题？请详细描述…"
                  className="w-full resize-none rounded-lg border border-background-700 bg-background-950 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                />
                <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full cursor-pointer whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "submitting" ? "提交中…" : "发送留言"}
              </button>
            </form>
          </div>

          <div className="mt-8 text-center">
            <Link to="/" className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-primary-400 hover:text-primary-300">
              <i className="ri-arrow-left-line" /> 返回首页
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}